<template>
  <div class="modal fade" id="stationEditModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="stationEditModalLabel">
            {{ localStation ? '编辑站点: ' + localStation.name : '编辑站点' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body" v-if="localStation">
          <form id="stationEditForm">
            <div class="mb-3">
              <label class="form-label">站点名称</label>
              <input type="text" class="form-control" v-model="localStation.name">
            </div>
            <div class="mb-3" v-if="languageSettings.enabled && visibleLanguages.length > 1">
              <template v-for="(lang, idx) in visibleLanguages" :key="lang.code || idx">
                <div v-if="lang && lang.code && lang.code !== 'zh'">
                  <label class="form-label">{{ lang.name }}站名</label>
                  <input type="text" class="form-control" :placeholder="lang.name + '站名'" v-model="localStation['name_' + lang.code]">
                </div>
              </template>
            </div>
            
            <ul class="nav nav-tabs mb-3" id="stationEditTabs" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link active" id="audio-edit-tab" data-bs-toggle="tab" data-bs-target="#audio-edit" type="button" role="tab">音频配置</button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link" id="departure-edit-tab" data-bs-toggle="tab" data-bs-target="#departure-edit" type="button" role="tab">出站语</button>
              </li>
              <li class="nav-item" role="presentation" v-if="!isFirstStation">
                <button class="nav-link" id="arrival-edit-tab" data-bs-toggle="tab" data-bs-target="#arrival-edit" type="button" role="tab">进站语</button>
              </li>
              <li class="nav-item" role="presentation" v-if="!hideAdvancedSettings">
                <button class="nav-link" id="advanced-edit-tab" data-bs-toggle="tab" data-bs-target="#advanced-edit" type="button" role="tab">高级设置</button>
              </li>
            </ul>
            
            <div class="tab-content" id="stationEditTabsContent">
              <!-- 音频配置 -->
              <div class="tab-pane fade show active" id="audio-edit" role="tabpanel">
                <div class="mb-3">
                  <h6>音频文件选择</h6>
                  <div class="mb-3">
                  <div class="row mb-2">
                    <div class="col-6">
                      <label class="form-label">可用音频列表</label>
                      <select class="form-select" size="6" v-model="selectedAudio">
                        <template v-for="folder in audioFolders" :key="folder.name">
                          <optgroup :label="folder.name" v-if="folder.audios && folder.audios.length > 0">
                            <option v-for="audio in folder.audios" :key="audio.name" :value="audio">
                              {{ audio.name }}
                            </option>
                          </optgroup>
                        </template>
                        <optgroup label="暂无音频文件" v-if="!audioFolders.length || audioFolders.every(f => !f.audios || !f.audios.length)">
                          <option disabled>请先选择项目文件夹</option>
                        </optgroup>
                      </select>
                      <!-- 按钮移动到可用音频列表下方 -->
                      <div class="d-flex gap-2 mt-2">
                        <button type="button" class="btn btn-sm btn-primary" @click="addToStationSequence">
                          添加
                        </button>
                        <button type="button" class="btn btn-sm btn-danger" @click="removeFromStationSequence" :disabled="selectedStationAudio === null">
                          移除
                        </button>
                      </div>
                    </div>
                    <div class="col-6">
                      <label class="form-label">站点播放列表</label>
                      <div 
                        class="sequence-list" 
                        @drop="onStationDrop" 
                        @dragover="onDragOver"
                        @dragenter="onDragEnter"
                        @dragleave="onDragLeave"
                      >
                        <div 
                          v-for="(audio, index) in localStation.stationAudioSequence" 
                          :key="index"
                          class="sequence-item"
                          :class="{ 
                            active: selectedStationAudio === index,
                            'drag-over': stationDraggedIndex !== null && stationDraggedIndex !== index && stationDropIndicatorIndex === index
                          }"
                          draggable="true"
                          @dragstart="onStationDragStart($event, index)"
                          @click="selectedStationAudio = index"
                        >
                          <span class="drag-handle">≡</span>
                          <span class="sequence-number">{{ index + 1 }}.</span>
                          <span class="audio-name" :title="audioFullPath(audio)">{{ audioLabel(audio) }}</span>
                        </div>
                        <!-- 拖拽辅助线 -->
                        <div 
                          v-if="stationDropIndicatorIndex !== null && stationDropIndicatorIndex === localStation.stationAudioSequence.length" 
                          class="drop-indicator"
                        ></div>
                      </div>
                      <div class="form-text">按顺序播放音频，可拖拽调整顺序</div>
                    </div>
                  </div>
                    <div class="row">
                      <div class="col d-flex justify-content-end align-items-center gap-2">
                        <button type="button" class="btn btn-sm btn-primary" @click="previewStationAudioSequence" :disabled="!localStation.stationAudioSequence || localStation.stationAudioSequence.length===0">
                          <i class="bi bi-play-fill"></i> 预览
                        </button>
                        <button type="button" class="btn btn-sm btn-success" @click="exportStationAudioSequence" :disabled="!localStation.stationAudioSequence || localStation.stationAudioSequence.length===0 || isExportingAudio">
                          <i class="bi bi-download"></i> 导出
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 进站语配置 -->
              <div class="tab-pane fade" id="arrival-edit" role="tabpanel" v-if="!isFirstStation">
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="customArrivalCheck" v-model="localStation.customArrival" @change="onCustomSettingChange">
                  <label class="form-check-label" for="customArrivalCheck">
                    使用自定义进站语
                  </label>
                </div>
                
                <div v-if="localStation.customArrival" class="mb-3">
                
                  <!-- 进站语音频配置 -->
                  <div class="mt-3">
                    <h6>进站语音频配置</h6>
                    <div class="mb-3">
                      <div class="row mb-2">
                        <div class="col-6">
                          <label class="form-label">可用音频列表</label>
                          <select class="form-select" size="6" v-model="selectedArrivalAudio">
                            <template v-for="folder in audioFolders" :key="folder.name">
                              <optgroup :label="folder.name" v-if="folder.audios && folder.audios.length > 0">
                                <option v-for="audio in folder.audios" :key="audio.name" :value="audio">
                                  {{ audio.name }}
                                </option>
                              </optgroup>
                            </template>
                            <optgroup label="暂无音频文件" v-if="!audioFolders.length || audioFolders.every(f => !f.audios || !f.audios.length)">
                              <option disabled>请先选择项目文件夹</option>
                            </optgroup>
                          </select>
                          <!-- 按钮移到可用音频列表下方 -->
                          <div class="d-flex gap-2 mt-2">
                            <button type="button" class="btn btn-sm btn-primary" @click="addToArrivalSequence">
                              添加
                            </button>
                            <button type="button" class="btn btn-sm btn-danger" @click="removeFromArrivalSequence" :disabled="selectedArrivalSequenceAudio === null">
                              移除
                            </button>
                          </div>
                        </div>
                        <div class="col-6">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="form-label mb-0">音频播放顺序</label>
                            <div class="d-flex align-items-center gap-2">
                              <button type="button" class="btn btn-sm btn-outline-secondary" @click="openReuseModal('arrival')">复用</button>
                              <button type="button" class="btn btn-sm btn-primary" @click="previewArrivalAudioSequence" :disabled="!localStation.arrivalAudioSequence || localStation.arrivalAudioSequence.length === 0">
                                <i class="bi bi-play-fill"></i> 预览
                              </button>
                              <button type="button" class="btn btn-sm btn-success" @click="exportArrivalAudioSequence" :disabled="!localStation.arrivalAudioSequence || localStation.arrivalAudioSequence.length === 0 || isExportingAudio">
                                <i class="bi bi-download"></i> 导出
                              </button>
                            </div>
                          </div>
                          <div 
                            class="sequence-list" 
                            @drop="onArrivalDrop" 
                            @dragover="onDragOver"
                            @dragenter="onDragEnter"
                            @dragleave="onDragLeave"
                          >
                            <div 
                              v-for="(audio, index) in localStation.arrivalAudioSequence" 
                              :key="index"
                              class="sequence-item"
                              :class="{ 
                                active: selectedArrivalSequenceAudio === index,
                                'drag-over': arrivalDraggedIndex !== null && arrivalDraggedIndex !== index && arrivalDropIndicatorIndex === index
                              }"
                              draggable="true"
                              @dragstart="onArrivalDragStart($event, index)"
                              @click="selectedArrivalSequenceAudio = index"
                            >
                              <span class="drag-handle">≡</span>
                              <span class="sequence-number">{{ index + 1 }}.</span>
                              <span class="audio-name" :title="audioFullPath(audio)">{{ audioLabel(audio) }}</span>
                            </div>
                            <!-- 拖拽辅助线 -->
                            <div 
                              v-if="arrivalDropIndicatorIndex !== null && arrivalDropIndicatorIndex === localStation.arrivalAudioSequence.length" 
                              class="drop-indicator"
                            ></div>
                          </div>
                          <div class="form-text">按顺序播放音频，可拖拽调整顺序</div>
                        </div>
                      </div>
                      <!-- 添加文件夹占位符 -->
                      <div class="row mt-3">
                        <div class="col-12">
                          <h6>占位符</h6>
                          <template v-for="folder in audioFolders" :key="`arrival-${folder.name}`">
                            <div v-if="folder.audios && folder.audios.length > 0" class="mb-2">
                              <small class="text-muted">{{ folder.name }}文件夹：</small><br>
                              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholderToArrival(folder.name, 'current')">
                                {{ folder.name }}-当前站
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholderToArrival(folder.name, 'terminal')">
                                {{ folder.name }}-终点站
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholderToArrival(folder.name, 'first')">
                                {{ folder.name }}-起点站
                              </button>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>
              
              <!-- 出站语配置 -->
              <div class="tab-pane fade" id="departure-edit" role="tabpanel">
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="customDepartureCheck" v-model="localStation.customDeparture" @change="onCustomSettingChange">
                  <label class="form-check-label" for="customDepartureCheck">
                    使用自定义出站语
                  </label>
                </div>
                
                <div v-if="localStation.customDeparture" class="mb-3">
                
                  <!-- 出站语音频配置 -->
                  <div class="mt-3">
                    <h6>出站语音频配置</h6>
                    <div class="mb-3">
                      <div class="row mb-2">
                        <div class="col-6">
                          <label class="form-label">可用音频列表</label>
                          <select class="form-select" size="6" v-model="selectedDepartureAudio">
                            <template v-for="folder in audioFolders" :key="folder.name">
                              <optgroup :label="folder.name" v-if="folder.audios && folder.audios.length > 0">
                                <option v-for="audio in folder.audios" :key="audio.name" :value="audio">
                                  {{ audio.name }}
                                </option>
                              </optgroup>
                            </template>
                            <optgroup label="暂无音频文件" v-if="!audioFolders.length || audioFolders.every(f => !f.audios || !f.audios.length)">
                              <option disabled>请先选择项目文件夹</option>
                            </optgroup>
                          </select>
                          <!-- 按钮移到可用音频列表下方 -->
                          <div class="d-flex gap-2 mt-2">
                            <button type="button" class="btn btn-sm btn-primary" @click="addToDepartureSequence">
                              添加
                            </button>
                            <button type="button" class="btn btn-sm btn-danger" @click="removeFromDepartureSequence" :disabled="selectedDepartureSequenceAudio === null">
                              移除
                            </button>
                          </div>
                        </div>
                        <div class="col-6">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="form-label mb-0">音频播放顺序</label>
                            <div class="d-flex align-items-center gap-2">
                              <button type="button" class="btn btn-sm btn-outline-secondary" @click="openReuseModal('departure')">复用</button>
                              <button type="button" class="btn btn-sm btn-primary" @click="previewDepartureAudioSequence" :disabled="!localStation.departureAudioSequence || localStation.departureAudioSequence.length === 0">
                                <i class="bi bi-play-fill"></i> 预览
                              </button>
                              <button type="button" class="btn btn-sm btn-success" @click="exportDepartureAudioSequence" :disabled="!localStation.departureAudioSequence || localStation.departureAudioSequence.length === 0 || isExportingAudio">
                                <i class="bi bi-download"></i> 导出
                              </button>
                            </div>
                          </div>
                          <div 
                            class="sequence-list" 
                            @drop="onDepartureDrop" 
                            @dragover="onDragOver"
                            @dragenter="onDragEnter"
                            @dragleave="onDragLeave"
                          >
                            <div 
                              v-for="(audio, index) in localStation.departureAudioSequence" 
                              :key="index"
                              class="sequence-item"
                              :class="{ 
                                active: selectedDepartureSequenceAudio === index,
                                'drag-over': departureDraggedIndex !== null && departureDraggedIndex !== index && departureDropIndicatorIndex === index
                              }"
                              draggable="true"
                              @dragstart="onDepartureDragStart($event, index)"
                              @click="selectedDepartureSequenceAudio = index"
                            >
                              <span class="drag-handle">≡</span>
                              <span class="sequence-number">{{ index + 1 }}.</span>
                              <span class="audio-name" :title="audioFullPath(audio)">{{ audioLabel(audio) }}</span>
                            </div>
                            <!-- 拖拽辅助线 -->
                            <div 
                              v-if="departureDropIndicatorIndex !== null && departureDropIndicatorIndex === localStation.departureAudioSequence.length" 
                              class="drop-indicator"
                            ></div>
                          </div>
                          <div class="form-text">按顺序播放音频，可拖拽调整顺序</div>
                        </div>
                      </div>
                      <!-- 添加文件夹占位符 -->
                      <div class="row mt-3">
                        <div class="col-12">
                          <h6>占位符</h6>
                          <template v-for="folder in audioFolders" :key="`departure-${folder.name}`">
                            <div v-if="folder.audios && folder.audios.length > 0" class="mb-2">
                              <small class="text-muted">{{ folder.name }}文件夹：</small><br>
                              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholderToDeparture(folder.name, 'current')">
                                {{ folder.name }}-当前站
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholderToDeparture(folder.name, 'terminal')">
                                {{ folder.name }}-终点站
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholderToDeparture(folder.name, 'first')">
                                {{ folder.name }}-起点站
                              </button>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>
              
              <!-- 高级设置 -->
              <div class="tab-pane fade" id="advanced-edit" role="tabpanel" v-if="!hideAdvancedSettings">
                <div class="mb-3">
                  <h6>地理位置信息</h6>
                  <div class="row mb-3">
                    <div class="col-md-6">
                      <label class="form-label">纬度</label>
                      <input type="number" class="form-control" v-model="localStation.latitude" step="0.000001" placeholder="" readonly>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">经度</label>
                      <input type="number" class="form-control" v-model="localStation.longitude" step="0.000001" placeholder="" readonly>
                    </div>
                  </div>
                  <div class="form-text mb-3">地理位置信息为预留功能，暂未启用。</div>
                </div>
                
                <div class="mb-3">
                  <h6>报站距离设置</h6>
                  <div class="row mb-3">
                    <div class="col-md-6">
                      <label class="form-label">进站报站距离 (米)</label>
                      <input type="number" class="form-control" v-model="localStation.arrivalDistance" min="0" placeholder="" readonly>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">出站报站距离 (米)</label>
                      <input type="number" class="form-control" v-model="localStation.departureDistance" min="0" placeholder="" readonly>
                    </div>
                  </div>
                  <div class="form-text mb-3">报站距离设置为预留功能，暂未启用。</div>
                </div>
                
                <div class="mb-3">
                  <h6>LED显示内容</h6>
                  <div class="mb-3">
                    <label class="form-label">进站LED显示内容</label>
                    <!-- 主语言输入框 -->
                    <div class="mb-2">
                      <label class="form-label small">中文</label>
                      <input type="text" class="form-control" 
                             v-model="localStation.arrivalLEDContent" 
                             placeholder="输入进站时LED显示的内容（中文）" 
                             ref="arrivalLEDInput"
                             @focus="setLEDFocusedField('arrivalLEDContent', 'zh')">
                    </div>
                    <!-- 多语言输入框 -->
                    <template v-if="languageSettings.enabled && visibleLanguages.length > 1">
                      <div v-for="(lang, idx) in visibleLanguages" :key="'arrival-led-lang-' + (lang.code || idx)" class="mb-2">
                        <div v-if="lang && lang.code && lang.code !== 'zh'">
                          <label class="form-label small">{{ lang.name }}</label>
                          <input type="text" class="form-control" 
                                 v-model="localStation['arrivalLEDContent_' + lang.code]" 
                                 :placeholder="`输入进站时LED显示的内容（${lang.name}）`"
                                 @focus="setLEDFocusedField('arrivalLEDContent', lang.code)">
                        </div>
                      </div>
                    </template>
                    
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">出站LED显示内容</label>
                    <!-- 主语言输入框 -->
                    <div class="mb-2">
                      <label class="form-label small">中文</label>
                      <input type="text" class="form-control" 
                             v-model="localStation.departureLEDContent" 
                             placeholder="输入出站时LED显示的内容（中文）" 
                             ref="departureLEDInput"
                             @focus="setLEDFocusedField('departureLEDContent', 'zh')">
                    </div>
                    <!-- 多语言输入框 -->
                    <template v-if="languageSettings.enabled && visibleLanguages.length > 1">
                      <div v-for="(lang, idx) in visibleLanguages" :key="'departure-led-lang-' + (lang.code || idx)" class="mb-2">
                        <div v-if="lang && lang.code && lang.code !== 'zh'">
                          <label class="form-label small">{{ lang.name }}</label>
                          <input type="text" class="form-control" 
                                 v-model="localStation['departureLEDContent_' + lang.code]" 
                                 :placeholder="`输入出站时LED显示的内容（${lang.name}）`"
                                 @focus="setLEDFocusedField('departureLEDContent', lang.code)">
                        </div>
                      </div>
                    </template>
                    
                  </div>
                  <div class="form-text">如果留空，将使用默认的站名显示。</div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">取消</button>
          <button type="button" class="btn btn-primary" @click="saveStation">保存</button>
        </div>
      </div>
    </div>
  </div>
  <!-- 复用序列弹窗 -->
  <div class="modal fade" id="reuseSequenceModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">复用音频序列</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">复用目标</label>
            <div><span class="badge bg-secondary">{{ reuseTargetLabel }}</span></div>
          </div>
          <div class="mb-3">
            <label class="form-label">来源站点（{{ sourceSequenceTypeLabel }}）</label>
            <select class="form-select" v-model="reuseSelectedSource">
              <option disabled value="">请选择来源站点</option>
              <option v-for="(s, idx) in stations" :key="'reuse-s-'+idx" :value="idx" :disabled="isSameStation(idx)">
                {{ idx + 1 }}. {{ s && s.name ? s.name : ('站点'+(idx+1)) }}
              </option>
            </select>
            <div class="form-text">占位符原样保留；仅支持从同类型序列复用。</div>
          </div>
          <div class="mb-3">
            <label class="form-label">复用方式</label>
            <div class="d-flex gap-3">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="reuseModeReplace" value="replace" v-model="reuseMode">
                <label class="form-check-label" for="reuseModeReplace">覆盖</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="radio" id="reuseModeAppend" value="append" v-model="reuseMode">
                <label class="form-check-label" for="reuseModeAppend">追加</label>
              </div>
            </div>
          </div>
          <div class="alert alert-warning" v-if="reuseWarning">{{ reuseWarning }}</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">取消</button>
          <button type="button" class="btn btn-primary" @click="applyReuse" :disabled="reuseSelectedSource === '' || reuseSelectedSource === null">应用</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StationEditModal',
  emits: ['save'],
  props: {
    station: {
      type: Object,
      default: null
    },
    stations: {
      type: Array,
      default: () => []
    },
    audioFolders: {
      type: Array,
      required: true
    },
    languageSettings: {
      type: Object,
      required: true
    },
    hideAdvancedSettings: {
      type: Boolean,
      default: false
    },
    // 模板与方向相关的输入，用于复用时回落默认序列
    defaultArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    defaultDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    upArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    downArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    upDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    downDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    separateDirections: {
      type: Object,
      default: () => ({ arrival: false, departure: false, welcome: false, terminal: false })
    },
    currentDirection: {
      type: String,
      default: 'up'
    }
  },
  data() {
    return {
      localStation: null,
      selectedAudio: null,
      selectedLanguage: null,
      useTemplate: false,
      // LED焦点跟踪
      ledFocusedField: null,
      ledFocusedLangCode: null,
      selectedStationAudio: null,
      isPlayingStation: false,
      playProgressStation: 0,
      isExportingAudio: false,
      currentStationAudioIndex: 0,
      stationAudioElements: [],
      activeTab: 'general',
      selectedArrivalAudio: null,
      selectedArrivalSequenceAudio: null,
      selectedDepartureAudio: null,
      selectedDepartureSequenceAudio: null,
      isPlayingArrival: false,
      isPlayingDeparture: false,
      playProgressArrival: 0,
      playProgressDeparture: 0,
      currentArrivalAudioIndex: 0,
      currentDepartureAudioIndex: 0,
      arrivalAudioElements: [],
      departureAudioElements: [],
      languages: [
        { name: '中文', code: 'zh' },
        { name: '英文', code: 'en' },
        { name: '日文', code: 'ja' },
        { name: '韩文', code: 'ko' },
        { name: '俄文', code: 'ru' }
      ],
      // 拖拽相关数据
      stationDraggedIndex: null,
      stationDropIndicatorIndex: null,
      arrivalDraggedIndex: null,
      arrivalDropIndicatorIndex: null,
      departureDraggedIndex: null,
      departureDropIndicatorIndex: null,
      // 复用弹窗相关
      reuseModal: null,
      reuseTarget: 'station',
      reuseSelectedSource: '',
      reuseMode: 'replace',
      reuseWarning: ''
    };
  },
  computed: {
    canMoveUpArrival() {
      return this.selectedArrivalSequenceAudio !== null && this.selectedArrivalSequenceAudio > 0;
    },
    canMoveDownArrival() {
      return this.selectedArrivalSequenceAudio !== null && 
             this.selectedArrivalSequenceAudio < this.localStation.arrivalAudioSequence.length - 1;
    },
    canMoveUpDeparture() {
      return this.selectedDepartureSequenceAudio !== null && this.selectedDepartureSequenceAudio > 0;
    },
    canMoveDownDeparture() {
      return this.selectedDepartureSequenceAudio !== null && 
             this.selectedDepartureSequenceAudio < this.localStation.departureAudioSequence.length - 1;
    },
    canMoveUpStation() {
      return this.selectedStationAudio !== null && this.selectedStationAudio > 0;
    },
    canMoveDownStation() {
      return this.selectedStationAudio !== null && 
             this.selectedStationAudio < this.localStation.stationAudioSequence.length - 1;
    },
    isFirstStation() {
      return this.localStation && this.localStation.index === 0;
    },
    visibleLanguages() {
      if (!this.languageSettings || !this.languageSettings.languages) return [];
      // 确保语言数量为数字类型，避免 undefined 错误
      const count = parseInt(this.languageSettings.count) || 1;
      return this.languageSettings.languages.slice(0, count);
    },
    reuseTargetLabel() {
      if (this.reuseTarget === 'station') return '站点播放列表';
      if (this.reuseTarget === 'arrival') return '进站语';
      if (this.reuseTarget === 'departure') return '出站语';
      return '';
    },
    sourceSequenceTypeLabel() {
      if (this.reuseTarget === 'station') return '站点播放列表';
      if (this.reuseTarget === 'arrival') return '进站语';
      if (this.reuseTarget === 'departure') return '出站语';
      return '';
    }
  },
  watch: {
    station: {
      handler(newStation) {
        if (newStation) {
          // 深拷贝以避免直接修改原数据
          this.localStation = JSON.parse(JSON.stringify(newStation));
          
          // 确保每个必要的字段都存在
          if (!this.localStation.arrivalAudioSequence) {
            this.localStation.arrivalAudioSequence = [];
          }
          
          if (!this.localStation.departureAudioSequence) {
            this.localStation.departureAudioSequence = [];
          }
          
          if (!this.localStation.stationAudioSequence) {
            this.localStation.stationAudioSequence = [];
          }
          
          // 初始化多语言LED字段
          if (this.languageSettings && this.languageSettings.enabled && this.languageSettings.languages) {
            this.languageSettings.languages.forEach(lang => {
              if (lang && lang.code && lang.code !== 'zh') {
                // 为每种语言初始化LED内容字段
                if (this.localStation['arrivalLEDContent_' + lang.code] === undefined) {
                  this.localStation['arrivalLEDContent_' + lang.code] = '';
                }
                if (this.localStation['departureLEDContent_' + lang.code] === undefined) {
                  this.localStation['departureLEDContent_' + lang.code] = '';
                }
              }
            });
          }
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      try {
        // 在DOM完全加载后初始化Bootstrap模态框
        const modalElement = document.getElementById('stationEditModal');
        if (window.bootstrap && modalElement) {
          this.modal = new window.bootstrap.Modal(modalElement);
          console.log('模态框初始化成功');
        } else {
          console.error('Bootstrap未加载或模态框元素未找到');
        }
        // 初始化复用弹窗
        const reuseEl = document.getElementById('reuseSequenceModal');
        if (window.bootstrap && reuseEl) {
          this.reuseModal = new window.bootstrap.Modal(reuseEl);
        }
      } catch (error) {
        console.error('初始化模态框时出错:', error);
      }
    });
  },
  methods: {
    openReuseModal(target) {
      try {
        this.reuseTarget = target;
        this.reuseSelectedSource = '';
        this.reuseMode = 'replace';
        this.reuseWarning = target === 'arrival' && this.isFirstStation ? '当前为起点站，通常无需设置进站语' : '';
        if (!this.reuseModal) {
          const reuseEl = document.getElementById('reuseSequenceModal');
          if (window.bootstrap && reuseEl) {
            this.reuseModal = new window.bootstrap.Modal(reuseEl);
          }
        }
        if (this.reuseModal) this.reuseModal.show();
      } catch (e) {
        console.error('打开复用弹窗失败:', e);
      }
    },
    isSameStation(index) {
      if (!this.stations || !this.localStation) return false;
      const s = this.stations[index];
      return s && this.localStation && s.id === this.localStation.id;
    },
    getSequenceByTarget(station, target) {
      if (!station) return [];
      if (target === 'station') return station.stationAudioSequence || [];
      if (target === 'arrival') return station.arrivalAudioSequence || [];
      if (target === 'departure') return station.departureAudioSequence || [];
      return [];
    },
    normalizeItem(item) {
      if (typeof item === 'string' && item.startsWith('blob:')) return this.convertBlobToPath(item);
      return item;
    },
    applyReuse() {
      try {
        if (this.reuseSelectedSource === '' || this.reuseSelectedSource === null) return;
        const sourceStation = this.stations && this.stations[this.reuseSelectedSource] ? this.stations[this.reuseSelectedSource] : null;
        if (!sourceStation) { alert('未找到来源站点'); return; }

        // 1) 先取来源站点的对应序列
        let src = this.getSequenceByTarget(sourceStation, this.reuseTarget).map(this.normalizeItem);

        // 2) 若来源站为默认排布或序列为空，则回落到模板默认/上下行序列
        const dir = (this.currentDirection === 'down') ? 'down' : 'up';
        const isArrival = this.reuseTarget === 'arrival';
        const isDeparture = this.reuseTarget === 'departure';
        const needsFallback =
          (isArrival && (sourceStation.customArrival === false || !Array.isArray(src) || src.length === 0)) ||
          (isDeparture && (sourceStation.customDeparture === false || !Array.isArray(src) || src.length === 0));

        if (needsFallback) {
          if (isArrival) {
            if (this.separateDirections && this.separateDirections.arrival) {
              src = dir === 'up' ? [...this.upArrivalAudioSequence] : [...this.downArrivalAudioSequence];
            } else {
              src = [...this.defaultArrivalAudioSequence];
            }
          } else if (isDeparture) {
            if (this.separateDirections && this.separateDirections.departure) {
              src = dir === 'up' ? [...this.upDepartureAudioSequence] : [...this.downDepartureAudioSequence];
            } else {
              src = [...this.defaultDepartureAudioSequence];
            }
          }
        }

        const targetField = this.reuseTarget === 'station' ? 'stationAudioSequence' : (isArrival ? 'arrivalAudioSequence' : 'departureAudioSequence');
        if (!this.localStation[targetField]) this.localStation[targetField] = [];

        if (this.reuseMode === 'replace') {
          this.localStation[targetField] = [...src];
        } else {
          // 默认采用追加模式
          this.localStation[targetField] = [...this.localStation[targetField], ...src];
        }

        // 自动保存但不关闭主编辑弹窗
        this.autoSave();
        if (this.reuseModal) this.reuseModal.hide();
      } catch (e) {
        console.error('应用复用失败:', e);
        alert('应用复用失败: ' + (e && e.message ? e.message : e));
      }
    },
    show() {
      // 确保localStation包含所有必要字段
      if (this.station) {
        this.localStation = JSON.parse(JSON.stringify(this.station));
        
        // 确保每个必要的字段都存在
        if (!this.localStation.arrivalAudioSequence) {
          this.localStation.arrivalAudioSequence = [];
        }
        
        if (!this.localStation.departureAudioSequence) {
          this.localStation.departureAudioSequence = [];
        }
        
        if (!this.localStation.stationAudioSequence) {
          this.localStation.stationAudioSequence = [];
        }
        
        // 初始化多语言LED字段
        if (this.languageSettings && this.languageSettings.enabled && this.languageSettings.languages) {
          this.languageSettings.languages.forEach(lang => {
            if (lang && lang.code && lang.code !== 'zh') {
              // 为每种语言初始化LED内容字段
              if (this.localStation['arrivalLEDContent_' + lang.code] === undefined) {
                this.localStation['arrivalLEDContent_' + lang.code] = '';
              }
              if (this.localStation['departureLEDContent_' + lang.code] === undefined) {
                this.localStation['departureLEDContent_' + lang.code] = '';
              }
            }
          });
        }
      }
      
      if (!this.modal) {
        // 如果模态框未初始化，尝试重新初始化
        try {
          const modalElement = document.getElementById('stationEditModal');
          if (window.bootstrap && modalElement) {
            this.modal = new window.bootstrap.Modal(modalElement);
            console.log('模态框初始化成功(延迟初始化)');
          } else {
            console.error('无法初始化模态框');
            alert('无法打开编辑窗口，请刷新页面重试');
            return;
          }
        } catch (error) {
          console.error('延迟初始化模态框失败:', error);
          alert('无法打开编辑窗口，请刷新页面重试');
          return;
        }
      }
      
      try {
        this.modal.show();
      } catch (error) {
        console.error('显示模态框失败:', error);
        alert('打开编辑窗口失败: ' + error.message);
      }
    },
    
    // 隐藏模态框
    hide() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    
    // 添加占位符到文本区域
    addPlaceholder(type, placeholder) {
      const field = type === 'arrival' ? 'arrivalTemplate' : 'departureTemplate';
      const textarea = document.querySelector(`textarea[v-model="localStation.${field}"]`);
      
      if (textarea) {
        const cursorPos = textarea.selectionStart;
        const textBefore = this.localStation[field].substring(0, cursorPos);
        const textAfter = this.localStation[field].substring(cursorPos);
        
        this.localStation[field] = textBefore + placeholder + textAfter;
        
        // 设置光标位置
        this.$nextTick(() => {
          textarea.focus();
          textarea.setSelectionRange(cursorPos + placeholder.length, cursorPos + placeholder.length);
        });
      } else {
        // 如果无法获取光标位置，就追加到末尾
        this.localStation[field] += placeholder;
      }
    },
    
    // 保存站点编辑
    saveStation() {
      // 确保所有相关字段的路径都已转换
      if (this.localStation.arrivalAudioSequence) {
        this.localStation.arrivalAudioSequence = this.localStation.arrivalAudioSequence.map(audio => this.convertBlobToPath(audio));
      }
      
      if (this.localStation.departureAudioSequence) {
        this.localStation.departureAudioSequence = this.localStation.departureAudioSequence.map(audio => this.convertBlobToPath(audio));
      }
      
      if (this.localStation.stationAudioSequence) {
        this.localStation.stationAudioSequence = this.localStation.stationAudioSequence.map(audio => this.convertBlobToPath(audio));
      }
      
      // 触发保存事件
      this.$emit('save', this.localStation);
      
      // 关闭模态框
      this.modal.hide();
    },
    
    // 获取用于显示的标签：目录/文件名
    audioLabel(item) {
      if (!item) return '';
      // 字符串且已包含目录
      if (typeof item === 'string' && item.includes('/')) {
        return item;
      }
      // 在文件夹中精确按 path 查找
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audio = folder.audios.find(a => a.path === item);
          if (audio) return `${folder.name}/${audio.name}`;
        }
      }
      // 退化显示
      if (typeof item === 'string') return item;
      return '';
    },

    // 完整 path（用于 tooltip）
    audioFullPath(item) {
      if (!item) return '';
      if (typeof item === 'string' && item.includes('/')) return item;
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audio = folder.audios.find(a => a.path === item);
          if (audio) return audio.path;
        }
      }
      return '';
    },
    
    // 添加音频到站点序列
    addToStationSequence() {
      if (!this.selectedAudio) return;
      if (!this.localStation.stationAudioSequence) {
        this.localStation.stationAudioSequence = [];
      }
      
      // 添加音频的path到序列
      this.localStation.stationAudioSequence.push(this.selectedAudio.path);
    },
    
    // 从站点序列中移除音频
    removeFromStationSequence() {
      if (this.selectedStationAudio === null) return;
      
      // 从播放顺序中移除
      this.localStation.stationAudioSequence.splice(this.selectedStationAudio, 1);
      
      // 重置选择
      this.selectedStationAudio = null;
    },
    
    // 向上移动站点音频
    moveUpStation() {
      if (!this.canMoveUpStation) return;
      
      const index = this.selectedStationAudio;
      const temp = this.localStation.stationAudioSequence[index];
      this.localStation.stationAudioSequence[index] = this.localStation.stationAudioSequence[index - 1];
      this.localStation.stationAudioSequence[index - 1] = temp;
      
      // 更新选择
      this.selectedStationAudio = index - 1;
    },
    
    // 向下移动站点音频
    moveDownStation() {
      if (!this.canMoveDownStation) return;
      
      const index = this.selectedStationAudio;
      const temp = this.localStation.stationAudioSequence[index];
      this.localStation.stationAudioSequence[index] = this.localStation.stationAudioSequence[index + 1];
      this.localStation.stationAudioSequence[index + 1] = temp;
      
      // 更新选择
      this.selectedStationAudio = index + 1;
    },
    
    // 预览站点音频序列
    previewStationAudioSequence() {
      if (this.isPlayingStation) {
        this.stopStationPlayback();
        return;
      }
      
      if (this.localStation.stationAudioSequence.length === 0) {
        alert('请先添加音频到站点播放序列');
        return;
      }
      
      // 开始播放
      this.isPlayingStation = true;
      this.currentStationAudioIndex = 0;
      this.playProgressStation = 0;
      
      // 清除之前的音频元素
      this.stationAudioElements = [];
      
      // 创建所有音频元素，处理占位符
      const validAudios = [];
      
      // 展开音频序列，处理占位符
      const expandedSequence = this.expandAudioSequence(this.localStation.stationAudioSequence);
      console.log('展开后的音频序列:', expandedSequence);
      
      for (const item of expandedSequence) {
        console.log('=== 站点播放预览：处理音频项 ===', item);
        const url = this.getAudioUrl(item);
        console.log('获取到的URL:', url);
        if (url && !url.startsWith('【')) { // 确保不是占位符
          const audio = new Audio(url);
          console.log('创建的Audio对象:', audio);
          this.stationAudioElements.push(audio);
          validAudios.push(item);
        } else {
          console.log('未获取到URL或仍为占位符，跳过此音频项');
        }
      }
      
      if (this.stationAudioElements.length === 0) {
        alert('无法找到有效的音频文件进行预览');
        this.isPlayingStation = false;
        return;
      }
      
      // 为每个音频添加结束事件，播放下一个
      this.stationAudioElements.forEach((audio, index) => {
        audio.addEventListener('ended', () => {
          if (index < this.stationAudioElements.length - 1) {
            this.currentStationAudioIndex = index + 1;
            this.playProgressStation = ((index + 1) / this.stationAudioElements.length) * 100;
            this.stationAudioElements[index + 1].play();
          } else {
            // 最后一个音频播放完毕
            this.isPlayingStation = false;
            this.currentStationAudioIndex = -1;
            this.playProgressStation = 100;
            
            // 延迟重置进度条
            setTimeout(() => {
              this.playProgressStation = 0;
            }, 1000);
          }
        });
      });
      
      // 开始播放第一个音频
      this.stationAudioElements[0].play();
    },
    
    // 停止站点播放
    stopStationPlayback() {
      if (!this.isPlayingStation) return;
      
      // 停止所有音频
      this.stationAudioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      
      // 重置状态
      this.isPlayingStation = false;
      this.currentStationAudioIndex = -1;
      this.playProgressStation = 0;
      this.stationAudioElements = [];
    },
    
    // 将blob URL转换为文件路径
    convertBlobToPath(blobUrl) {
      if (typeof blobUrl !== 'string' || !blobUrl.startsWith('blob:')) {
        return blobUrl; // 如果不是blob URL，直接返回
      }
      
      // 从所有音频文件夹中查找
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audio = folder.audios.find(a => a.url === blobUrl);
          if (audio) return audio.path;
        }
      }
      
      return blobUrl;
    },
    
    // 展开音频序列，将占位符替换为实际的音频路径
    expandAudioSequence(sequence) {
      const result = [];
      
      for (const item of sequence) {
        if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
          console.log('=== 展开占位符 ===', item);
          
          // 解析文件夹名
          const folderMatch = item.match(/【(.+?)-/);
          const folderName = folderMatch ? folderMatch[1] : '';
          console.log('解析文件夹名:', folderName);
          
          // 获取目标文件夹
          const targetFolder = this.audioFolders.find(f => f.name === folderName);
          console.log('找到目标文件夹:', targetFolder);
          
          // 解析站点类型并获取目标站点
          let targetStationName = '';
          let targetStation = null;
          
          if (item.includes('当前站')) {
            targetStationName = this.localStation.name;
            targetStation = this.localStation;
            console.log('当前站 - targetStation:', targetStation);
            console.log('当前站 - stationAudioSequence:', targetStation?.stationAudioSequence);
          } else if (item.includes('起点站')) {
            if (this.stations && this.stations.length > 0) {
              targetStation = this.stations[0];
              targetStationName = targetStation.name;
            } else {
              targetStationName = this.localStation.name;
              targetStation = this.localStation;
            }
            console.log('起点站 - targetStation:', targetStation);
          } else if (item.includes('终点站')) {
            if (this.stations && this.stations.length > 0) {
              targetStation = this.stations[this.stations.length - 1];
              targetStationName = targetStation.name;
            } else {
              targetStationName = this.localStation.name;
              targetStation = this.localStation;
            }
            console.log('终点站 - targetStation:', targetStation);
          }
          
          // *** 优先级1：检查目标站点是否有自定义播放列表 ***
          if (targetStation && targetStation.stationAudioSequence && targetStation.stationAudioSequence.length > 0) {
            console.log('找到自定义播放列表:', targetStation.stationAudioSequence);
            // 将整个自定义播放列表添加到结果中
            result.push(...targetStation.stationAudioSequence);
          } else {
            // *** 优先级2：如果没有自定义播放列表，按站点名查找单个音频文件 ***
            console.log('没有自定义播放列表，按站点名查找:', targetStationName);
            if (targetStationName && targetFolder && targetFolder.audios) {
              const audio = targetFolder.audios.find(a => a.name.replace(/\.(mp3|wav|ogg|m4a)$/i, '') === targetStationName);
              if (audio) {
                console.log('找到站点名对应音频:', audio.path);
                result.push(audio.path);
              } else {
                console.log('未找到站点名对应音频');
              }
            }
          }
        } else {
          // 不是占位符，直接添加
          result.push(item);
        }
      }
      
      return result;
    },

    // 获取实际的音频URL（用于预览）- 对于占位符，返回特殊标识
    getAudioUrl(item) {
      // 如果是占位符，返回占位符本身，让调用方处理
      if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
        return item; // 返回占位符，让调用方特殊处理
      }
      
      // 如果是路径格式，查找对应的URL
      if (typeof item === 'string' && item.includes('/')) {
        // 仅允许按 path 精确匹配，避免同名串目录
        for (const folder of this.audioFolders) {
          if (folder.audios) {
            const audio = folder.audios.find(a => a.path === item);
            if (audio) return audio.url;
          }
        }
        return null;
      }
      
      // 如果是blob URL，直接返回
      return item;
    },
    
    // 添加音频到序列
    addToArrivalSequence() {
      if (!this.selectedArrivalAudio) return;
      
      // 使用完整的音频路径，包含文件夹前缀
      const audioPath = this.selectedArrivalAudio.path;
      this.localStation.arrivalAudioSequence.push(audioPath);
    },
    
    // 从序列中移除音频
    removeFromArrivalSequence() {
      if (this.selectedArrivalSequenceAudio === null) return;
      
      // 从播放顺序中移除
      this.localStation.arrivalAudioSequence.splice(this.selectedArrivalSequenceAudio, 1);
      
      // 重置选择
      this.selectedArrivalSequenceAudio = null;
    },
    
    // 向上移动音频
    moveUpArrival() {
      if (!this.canMoveUpArrival) return;
      
      const index = this.selectedArrivalSequenceAudio;
      const temp = this.localStation.arrivalAudioSequence[index];
      this.localStation.arrivalAudioSequence[index] = this.localStation.arrivalAudioSequence[index - 1];
      this.localStation.arrivalAudioSequence[index - 1] = temp;
      
      // 更新选择
      this.selectedArrivalSequenceAudio = index - 1;
    },
    
    // 向下移动音频
    moveDownArrival() {
      if (!this.canMoveDownArrival) return;
      
      const index = this.selectedArrivalSequenceAudio;
      const temp = this.localStation.arrivalAudioSequence[index];
      this.localStation.arrivalAudioSequence[index] = this.localStation.arrivalAudioSequence[index + 1];
      this.localStation.arrivalAudioSequence[index + 1] = temp;
      
      // 更新选择
      this.selectedArrivalSequenceAudio = index + 1;
    },
    
    // 预览音频序列 - 修改为处理占位符
    previewArrivalAudioSequence() {
      if (this.isPlayingArrival) {
        this.stopArrivalPlayback();
        return;
      }
      
      if (this.localStation.arrivalAudioSequence.length === 0) {
        alert('请先添加音频到进站语播放序列');
        return;
      }
      
      // 开始播放
      this.isPlayingArrival = true;
      this.currentArrivalAudioIndex = 0;
      this.playProgressArrival = 0;
      
      // 清除之前的音频元素
      this.arrivalAudioElements = [];
      
      // 创建所有音频元素，处理占位符
      const validAudios = [];
      
      // 展开音频序列，处理占位符
      const expandedSequence = this.expandAudioSequence(this.localStation.arrivalAudioSequence);
      console.log('展开后的进站语音频序列:', expandedSequence);
      
      for (const item of expandedSequence) {
        console.log('=== 进站语播放预览：处理音频项 ===', item);
        const url = this.getAudioUrl(item);
        console.log('获取到的URL:', url);
        if (url && !url.startsWith('【')) { // 确保不是占位符
          const audio = new Audio(url);
          console.log('创建的Audio对象:', audio);
          this.arrivalAudioElements.push(audio);
          validAudios.push(item);
        } else {
          console.log('未获取到URL或仍为占位符，跳过此音频项');
        }
      }
      
      if (this.arrivalAudioElements.length === 0) {
        alert('无法找到有效的音频文件进行预览');
        this.isPlayingArrival = false;
        return;
      }
      
      // 为每个音频添加结束事件，播放下一个
      this.arrivalAudioElements.forEach((audio, index) => {
        audio.addEventListener('ended', () => {
          if (index < this.arrivalAudioElements.length - 1) {
            this.currentArrivalAudioIndex = index + 1;
            this.playProgressArrival = ((index + 1) / this.arrivalAudioElements.length) * 100;
            this.arrivalAudioElements[index + 1].play();
          } else {
            // 最后一个音频播放完毕
            this.isPlayingArrival = false;
            this.currentArrivalAudioIndex = -1;
            this.playProgressArrival = 100;
            
            // 延迟重置进度条
            setTimeout(() => {
              this.playProgressArrival = 0;
            }, 1000);
          }
        });
      });
      
      // 开始播放第一个音频
      this.arrivalAudioElements[0].play();
    },
    
    // 停止播放
    stopArrivalPlayback() {
      if (!this.isPlayingArrival) return;
      
      // 停止所有音频
      this.arrivalAudioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      
      // 重置状态
      this.isPlayingArrival = false;
      this.currentArrivalAudioIndex = -1;
      this.playProgressArrival = 0;
      this.arrivalAudioElements = [];
    },
    
    // 添加音频到序列
    addToDepartureSequence() {
      if (!this.selectedDepartureAudio) return;
      
      // 使用完整的音频路径，包含文件夹前缀
      const audioPath = this.selectedDepartureAudio.path;
      this.localStation.departureAudioSequence.push(audioPath);
    },
    
    // 从序列中移除音频
    removeFromDepartureSequence() {
      if (this.selectedDepartureSequenceAudio === null) return;
      
      // 从播放顺序中移除
      this.localStation.departureAudioSequence.splice(this.selectedDepartureSequenceAudio, 1);
      
      // 重置选择
      this.selectedDepartureSequenceAudio = null;
    },
    
    // 向上移动音频
    moveUpDeparture() {
      if (!this.canMoveUpDeparture) return;
      
      const index = this.selectedDepartureSequenceAudio;
      const temp = this.localStation.departureAudioSequence[index];
      this.localStation.departureAudioSequence[index] = this.localStation.departureAudioSequence[index - 1];
      this.localStation.departureAudioSequence[index - 1] = temp;
      
      // 更新选择
      this.selectedDepartureSequenceAudio = index - 1;
    },
    
    // 向下移动音频
    moveDownDeparture() {
      if (!this.canMoveDownDeparture) return;
      
      const index = this.selectedDepartureSequenceAudio;
      const temp = this.localStation.departureAudioSequence[index];
      this.localStation.departureAudioSequence[index] = this.localStation.departureAudioSequence[index + 1];
      this.localStation.departureAudioSequence[index + 1] = temp;
      
      // 更新选择
      this.selectedDepartureSequenceAudio = index + 1;
    },
    
    // 预览音频序列 - 修改为处理占位符
    previewDepartureAudioSequence() {
      if (this.isPlayingDeparture) {
        this.stopDeparturePlayback();
        return;
      }
      
      if (this.localStation.departureAudioSequence.length === 0) {
        alert('请先添加音频到出站语播放序列');
        return;
      }
      
      // 开始播放
      this.isPlayingDeparture = true;
      this.currentDepartureAudioIndex = 0;
      this.playProgressDeparture = 0;
      
      // 清除之前的音频元素
      this.departureAudioElements = [];
      
      // 创建所有音频元素，处理占位符
      const validAudios = [];
      
      // 展开音频序列，处理占位符
      const expandedSequence = this.expandAudioSequence(this.localStation.departureAudioSequence);
      console.log('展开后的出站语音频序列:', expandedSequence);
      
      for (const item of expandedSequence) {
        console.log('=== 出站语播放预览：处理音频项 ===', item);
        const url = this.getAudioUrl(item);
        console.log('获取到的URL:', url);
        if (url && !url.startsWith('【')) { // 确保不是占位符
          const audio = new Audio(url);
          console.log('创建的Audio对象:', audio);
          this.departureAudioElements.push(audio);
          validAudios.push(item);
        } else {
          console.log('未获取到URL或仍为占位符，跳过此音频项');
        }
      }
      
      if (this.departureAudioElements.length === 0) {
        alert('无法找到有效的音频文件进行预览');
        this.isPlayingDeparture = false;
        return;
      }
      
      // 为每个音频添加结束事件，播放下一个
      this.departureAudioElements.forEach((audio, index) => {
        audio.addEventListener('ended', () => {
          if (index < this.departureAudioElements.length - 1) {
            this.currentDepartureAudioIndex = index + 1;
            this.playProgressDeparture = ((index + 1) / this.departureAudioElements.length) * 100;
            this.departureAudioElements[index + 1].play();
          } else {
            // 最后一个音频播放完毕
            this.isPlayingDeparture = false;
            this.currentDepartureAudioIndex = -1;
            this.playProgressDeparture = 100;
            
            // 延迟重置进度条
            setTimeout(() => {
              this.playProgressDeparture = 0;
            }, 1000);
          }
        });
      });
      
      // 开始播放第一个音频
      this.departureAudioElements[0].play();
    },
    
    // 停止播放
    stopDeparturePlayback() {
      if (!this.isPlayingDeparture) return;
      
      // 停止所有音频
      this.departureAudioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      
      // 重置状态
      this.isPlayingDeparture = false;
      this.currentDepartureAudioIndex = -1;
      this.playProgressDeparture = 0;
      this.departureAudioElements = [];
    },

    async exportStationAudioSequence() {
      if (!this.localStation || !Array.isArray(this.localStation.stationAudioSequence) || this.localStation.stationAudioSequence.length === 0) {
        alert('请先添加音频到站点播放序列');
        return;
      }
      const name = this.localStation.name || 'station';
      await this.exportAudioSequence(this.localStation.stationAudioSequence, `${name}-station.wav`);
    },

    async exportArrivalAudioSequence() {
      if (!this.localStation || !Array.isArray(this.localStation.arrivalAudioSequence) || this.localStation.arrivalAudioSequence.length === 0) {
        alert('请先添加音频到进站语播放序列');
        return;
      }
      const name = this.localStation.name || 'station';
      await this.exportAudioSequence(this.localStation.arrivalAudioSequence, `${name}-arrival.wav`);
    },

    async exportDepartureAudioSequence() {
      if (!this.localStation || !Array.isArray(this.localStation.departureAudioSequence) || this.localStation.departureAudioSequence.length === 0) {
        alert('请先添加音频到出站语播放序列');
        return;
      }
      const name = this.localStation.name || 'station';
      await this.exportAudioSequence(this.localStation.departureAudioSequence, `${name}-departure.wav`);
    },

    async exportAudioSequence(sequence, defaultFileName) {
      if (this.isExportingAudio) return;
      this.isExportingAudio = true;
      try {
        const expandedSequence = this.expandAudioSequence(sequence);
        const audioBuffers = await this.loadAudioBuffers(expandedSequence);
        if (!audioBuffers || audioBuffers.length === 0) {
          alert('无法找到有效的音频文件进行导出');
          return;
        }

        const mergedBuffer = await this.mergeAudioBuffers(audioBuffers);
        if (!mergedBuffer) {
          alert('音频合并失败');
          return;
        }

        const wavBlob = this.encodeWav(mergedBuffer);
        if (!wavBlob) {
          alert('音频编码失败');
          return;
        }

        const fileName = defaultFileName || 'export.wav';
        this.triggerDownload(wavBlob, fileName);
      } catch (error) {
        console.error('音频导出失败:', error);
        alert('音频导出失败：' + (error && error.message ? error.message : '未知错误'));
      } finally {
        this.isExportingAudio = false;
      }
    },

    async loadAudioBuffers(sequence) {
      if (!sequence || sequence.length === 0) return [];

      const ContextClass = window.AudioContext || window.webkitAudioContext;
      if (!ContextClass) {
        alert('浏览器不支持导出音频');
        return [];
      }

      const audioContext = new ContextClass();
      const buffers = [];
      let referenceSampleRate = null;

      for (const item of sequence) {
        const url = this.getAudioUrl(item);
        if (!url || (typeof url === 'string' && url.startsWith('【'))) {
          continue;
        }

        try {
          const arrayBuffer = await this.fetchAudioData(url);
          if (!arrayBuffer) continue;
          const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
          if (!decoded) continue;

          if (referenceSampleRate === null) {
            referenceSampleRate = decoded.sampleRate;
          }

          let processedBuffer = decoded;
          if (decoded.sampleRate !== referenceSampleRate) {
            processedBuffer = await this.resampleAudioBuffer(decoded, referenceSampleRate);
          }

          if (processedBuffer) {
            buffers.push(processedBuffer);
          }
        } catch (error) {
          console.warn('加载音频失败，已跳过', item, error);
        }
      }

      try {
        if (audioContext && audioContext.state !== 'closed') {
          await audioContext.close();
        }
      } catch (closeError) {
        console.warn('关闭AudioContext失败', closeError);
      }

      return buffers;
    },

    async resampleAudioBuffer(audioBuffer, targetSampleRate) {
      try {
        const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OfflineContext) return audioBuffer;
        const length = Math.ceil(audioBuffer.length * targetSampleRate / audioBuffer.sampleRate);
        const offlineContext = new OfflineContext(audioBuffer.numberOfChannels, length, targetSampleRate);
        const source = offlineContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineContext.destination);
        source.start(0);
        return offlineContext.startRendering();
      } catch (error) {
        console.warn('重采样失败，使用原始音频', error);
        return audioBuffer;
      }
    },

    async fetchAudioData(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('网络请求失败: ' + response.statusText);
        }
        return await response.arrayBuffer();
      } catch (error) {
        console.warn('获取音频数据失败', url, error);
        return null;
      }
    },

    async mergeAudioBuffers(buffers) {
      if (!buffers || buffers.length === 0) return null;

      const ContextClass = window.AudioContext || window.webkitAudioContext;
      if (!ContextClass) {
        alert('浏览器不支持导出音频');
        return null;
      }

      const tempContext = new ContextClass();
      const sampleRate = buffers[0].sampleRate;
      const channelCount = Math.max(...buffers.map(buffer => buffer.numberOfChannels));
      const totalLength = buffers.reduce((sum, buffer) => sum + buffer.length, 0);

      const outputBuffer = tempContext.createBuffer(channelCount, totalLength, sampleRate);

      for (let channel = 0; channel < channelCount; channel++) {
        const channelData = outputBuffer.getChannelData(channel);
        let offset = 0;
        buffers.forEach(buffer => {
          const data = buffer.getChannelData(channel < buffer.numberOfChannels ? channel : 0);
          channelData.set(data, offset);
          offset += buffer.length;
        });
      }

      try {
        if (tempContext && tempContext.state !== 'closed') {
          await tempContext.close();
        }
      } catch (closeError) {
        console.warn('关闭临时AudioContext失败', closeError);
      }

      return outputBuffer;
    },

    encodeWav(audioBuffer) {
      if (!audioBuffer) return null;

      const numberOfChannels = audioBuffer.numberOfChannels;
      const sampleRate = audioBuffer.sampleRate;
      const samples = audioBuffer.length;
      const dataLength = samples * numberOfChannels * 2; // 16-bit
      const buffer = new ArrayBuffer(44 + dataLength);
      const view = new DataView(buffer);

      const interleaved = this.interleaveAudioData(audioBuffer, numberOfChannels, samples);
      this.writeWavHeader(view, sampleRate, numberOfChannels, dataLength);

      let offset = 44;
      for (let i = 0; i < interleaved.length; i++, offset += 2) {
        const sample = Math.max(-1, Math.min(1, interleaved[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      }

      return new Blob([view], { type: 'audio/wav' });
    },

    interleaveAudioData(audioBuffer, channelCount, samples) {
      const result = new Float32Array(samples * channelCount);
      const channels = [];
      for (let i = 0; i < channelCount; i++) {
        channels.push(audioBuffer.getChannelData(i < audioBuffer.numberOfChannels ? i : 0));
      }

      let offset = 0;
      for (let i = 0; i < samples; i++) {
        for (let channel = 0; channel < channelCount; channel++) {
          result[offset++] = channels[channel][i] || 0;
        }
      }
      return result;
    },

    writeWavHeader(view, sampleRate, channelCount, dataLength) {
      const blockAlign = channelCount * 2;
      const byteRate = sampleRate * blockAlign;

      this.writeString(view, 0, 'RIFF');
      view.setUint32(4, 36 + dataLength, true);
      this.writeString(view, 8, 'WAVE');
      this.writeString(view, 12, 'fmt ');
      view.setUint32(16, 16, true); // Subchunk1Size (PCM)
      view.setUint16(20, 1, true); // AudioFormat (PCM)
      view.setUint16(22, channelCount, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, byteRate, true);
      view.setUint16(32, blockAlign, true);
      view.setUint16(34, 16, true); // BitsPerSample
      this.writeString(view, 36, 'data');
      view.setUint32(40, dataLength, true);
    },

    writeString(view, offset, string) {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    },

    triggerDownload(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // 添加预设音频到序列
    addPresetAudio(type, preset) {
      let placeholder = '';
      
      // 如果是进站语且当前站点是起点站，显示警告
      if (type === 'arrival' && this.isFirstStation) {
        alert('起点站不需要设置进站语音，因为起点站进站时车上没有乘客。');
        return;
      }
      
      // 根据预设类型设置对应的占位符
      if (preset.startsWith('current')) {
        const langIndex = preset.length > 7 ? parseInt(preset.substring(7)) : 0;
        if (langIndex > 0) {
          placeholder = `【当前站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【当前站】';
        }
      }
      else if (preset.startsWith('terminal')) {
        const langIndex = preset.length > 8 ? parseInt(preset.substring(8)) : 0;
        if (langIndex > 0) {
          placeholder = `【终点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【终点站】';
        }
      }
      else if (preset.startsWith('first')) {
        const langIndex = preset.length > 5 ? parseInt(preset.substring(5)) : 0;
        if (langIndex > 0) {
          placeholder = `【起点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【起点站】';
        }
      }
      else if (preset === 'alias') {
        placeholder = '【副站名】';
      }

      // 添加到对应的序列中
      const sequence = type === 'arrival' ? this.localStation.arrivalAudioSequence : this.localStation.departureAudioSequence;
      sequence.push(placeholder);
    },

    addFolderPlaceholder(type, folderName, preset) {
      let placeholder = '';
      if (preset.startsWith('current')) {
        const langIndex = preset.length > 7 ? parseInt(preset.substring(7)) : 0;
        if (langIndex > 0) {
          placeholder = `【${folderName}-当前站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = `【${folderName}-当前站】`;
        }
      }
      else if (preset.startsWith('terminal')) {
        const langIndex = preset.length > 8 ? parseInt(preset.substring(8)) : 0;
        if (langIndex > 0) {
          placeholder = `【${folderName}-终点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = `【${folderName}-终点站】`;
        }
      }
      else if (preset.startsWith('first')) {
        const langIndex = preset.length > 5 ? parseInt(preset.substring(5)) : 0;
        if (langIndex > 0) {
          placeholder = `【${folderName}-起点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = `【${folderName}-起点站】`;
        }
      }
      else if (preset === 'alias') {
        placeholder = `【${folderName}-副站名】`;
      }

      const sequence = type === 'arrival' ? this.localStation.arrivalAudioSequence : this.localStation.departureAudioSequence;
      sequence.push(placeholder);
    },

    insertLEDPlaceholder(field, placeholder) {
      // 获取对应的input元素
      const refName = field === 'arrivalLEDContent' ? 'arrivalLEDInput' : 'departureLEDInput';
      const input = this.$refs[refName];
      if (input && input.selectionStart !== undefined) {
        const cursorPos = input.selectionStart;
        const value = this.localStation[field] || '';
        this.localStation[field] = value.slice(0, cursorPos) + placeholder + value.slice(cursorPos);
        this.$nextTick(() => {
          input.focus();
          input.setSelectionRange(cursorPos + placeholder.length, cursorPos + placeholder.length);
        });
      } else {
        // 无法获取光标时直接追加
        this.localStation[field] = (this.localStation[field] || '') + placeholder;
      }
    },

    insertLEDPlaceholderSmart(placeholder) {
      // 如果没有聚焦的字段，默认使用进站LED中文字段
      if (!this.ledFocusedField) {
        this.ledFocusedField = 'arrivalLEDContent';
        this.ledFocusedLangCode = 'zh';
      }

      // 确定目标字段名
      let targetField;
      if (this.ledFocusedLangCode === 'zh') {
        targetField = this.ledFocusedField;
      } else {
        targetField = this.ledFocusedField + '_' + this.ledFocusedLangCode;
      }

      // 确保字段存在
      if (!this.localStation[targetField]) {
        this.localStation[targetField] = '';
      }

      // 获取当前值和插入位置
      const currentValue = this.localStation[targetField];
      
      // 尝试获取对应的input元素来获取光标位置
      let input = null;
      if (this.ledFocusedLangCode === 'zh') {
        const refName = this.ledFocusedField === 'arrivalLEDContent' ? 'arrivalLEDInput' : 'departureLEDInput';
        input = this.$refs[refName];
      } else {
        // 对于多语言字段，使用querySelector查找
        input = document.querySelector(`input[v-model="localStation['${targetField}']"]`);
      }

      if (input && input.selectionStart !== undefined) {
        const cursorPos = input.selectionStart;
        this.localStation[targetField] = currentValue.slice(0, cursorPos) + placeholder + currentValue.slice(cursorPos);
        this.$nextTick(() => {
          input.focus();
          input.setSelectionRange(cursorPos + placeholder.length, cursorPos + placeholder.length);
        });
      } else {
        // 无法获取光标时直接追加
        this.localStation[targetField] += placeholder;
      }
    },

    setLEDFocusedField(field, langCode) {
      this.ledFocusedField = field;
      this.ledFocusedLangCode = langCode;
    },
    
    // 处理自定义设置变化
    onCustomSettingChange() {
      // 自动保存站点配置（不关闭模态框）
      this.autoSave();
    },
    
    // 自动保存站点数据（不关闭模态框）
    autoSave() {
      // 创建一个用于保存的站点副本
      const stationToSave = JSON.parse(JSON.stringify(this.localStation));
      
      // 确保所有相关字段的路径都已转换
      if (stationToSave.arrivalAudioSequence) {
        stationToSave.arrivalAudioSequence = stationToSave.arrivalAudioSequence.map(audio => this.convertBlobToPath(audio));
      }
      
      if (stationToSave.departureAudioSequence) {
        stationToSave.departureAudioSequence = stationToSave.departureAudioSequence.map(audio => this.convertBlobToPath(audio));
      }
      
      if (stationToSave.stationAudioSequence) {
        stationToSave.stationAudioSequence = stationToSave.stationAudioSequence.map(audio => this.convertBlobToPath(audio));
      }
      
      // 触发保存事件，但不关闭模态框
      this.$emit('save', stationToSave);
    },

    // 拖拽相关方法 - 进站语
    onArrivalDragStart(event, index) {
      this.arrivalDraggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },

    onArrivalDrop(event) {
      event.preventDefault();
      const dropIndex = this.getDropIndex(event);
      if (this.arrivalDraggedIndex !== null && dropIndex !== null && this.arrivalDraggedIndex !== dropIndex) {
        this.moveArrivalItem(this.arrivalDraggedIndex, dropIndex);
      }
      this.arrivalDraggedIndex = null;
      this.arrivalDropIndicatorIndex = null;
    },

    moveArrivalItem(fromIndex, toIndex) {
      const item = this.localStation.arrivalAudioSequence.splice(fromIndex, 1)[0];
      this.localStation.arrivalAudioSequence.splice(toIndex, 0, item);
    },

    // 拖拽相关方法 - 站点音频
    onStationDragStart(event, index) {
      this.stationDraggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },

    onStationDrop(event) {
      event.preventDefault();
      const dropIndex = this.getDropIndex(event);
      if (this.stationDraggedIndex !== null && dropIndex !== null && this.stationDraggedIndex !== dropIndex) {
        this.moveStationItem(this.stationDraggedIndex, dropIndex);
      }
      this.stationDraggedIndex = null;
      this.stationDropIndicatorIndex = null;
    },

    moveStationItem(fromIndex, toIndex) {
      const item = this.localStation.stationAudioSequence.splice(fromIndex, 1)[0];
      this.localStation.stationAudioSequence.splice(toIndex, 0, item);
    },

    // 拖拽相关方法 - 出站语
    onDepartureDragStart(event, index) {
      this.departureDraggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },

    onDepartureDrop(event) {
      event.preventDefault();
      const dropIndex = this.getDropIndex(event);
      if (this.departureDraggedIndex !== null && dropIndex !== null && this.departureDraggedIndex !== dropIndex) {
        this.moveDepartureItem(this.departureDraggedIndex, dropIndex);
      }
      this.departureDraggedIndex = null;
      this.departureDropIndicatorIndex = null;
    },

    moveDepartureItem(fromIndex, toIndex) {
      const item = this.localStation.departureAudioSequence.splice(fromIndex, 1)[0];
      this.localStation.departureAudioSequence.splice(toIndex, 0, item);
    },

    // 通用拖拽方法
    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      this.updateDropIndicator(event);
    },

    onDragEnter(event) {
      event.preventDefault();
      this.updateDropIndicator(event);
    },

    onDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.stationDropIndicatorIndex = null;
        this.arrivalDropIndicatorIndex = null;
        this.departureDropIndicatorIndex = null;
      }
    },

    updateDropIndicator(event) {
      const dropIndex = this.getDropIndex(event);
      // 根据容器的位置来判断是哪个序列
      const container = event.currentTarget;
      if (container.closest('#audio-edit')) {
        this.stationDropIndicatorIndex = dropIndex;
      } else if (container.closest('#arrival-edit')) {
        this.arrivalDropIndicatorIndex = dropIndex;
      } else if (container.closest('#departure-edit')) {
        this.departureDropIndicatorIndex = dropIndex;
      }
    },

    getDropIndex(event) {
      const items = event.currentTarget.querySelectorAll('.sequence-item');
      const rect = event.currentTarget.getBoundingClientRect();
      const y = event.clientY - rect.top;
      
      for (let i = 0; i < items.length; i++) {
        const itemRect = items[i].getBoundingClientRect();
        const itemY = itemRect.top - rect.top + itemRect.height / 2;
        if (y < itemY) {
          return i;
        }
      }
      return items.length;
    },

    // 文件夹占位符添加方法
    addFolderPlaceholderToArrival(folderName, stationType) {
      const placeholder = `【${folderName}-${stationType === 'current' ? '当前站' : stationType === 'terminal' ? '终点站' : '起点站'}】`;
      this.localStation.arrivalAudioSequence.push(placeholder);
    },

    addFolderPlaceholderToDeparture(folderName, stationType) {
      const placeholder = `【${folderName}-${stationType === 'current' ? '当前站' : stationType === 'terminal' ? '终点站' : '起点站'}】`;
      this.localStation.departureAudioSequence.push(placeholder);
    }
  }
};
</script>

<style scoped>
.audio-preview {
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

/* 拖拽相关样式 */
.sequence-list {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  min-height: 150px;
  max-height: 200px;
  overflow-y: auto;
  padding: 5px;
  background-color: #f8f9fa;
}

.sequence-item {
  display: flex;
  align-items: center;
  padding: 8px;
  margin-bottom: 2px;
  background-color: white;
  border-radius: 3px;
  cursor: move;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.sequence-item:hover {
  background-color: #e9ecef;
  border-color: #6c757d;
}

.sequence-item.active {
  background-color: #cfe2ff;
  border-color: #0d6efd;
}

.drag-handle {
  font-weight: bold;
  color: #6c757d;
  margin-right: 8px;
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.sequence-number {
  font-weight: bold;
  margin-right: 8px;
  color: #495057;
  min-width: 30px;
}

.audio-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9em;
}

/* 拖拽辅助线样式 */
.drop-indicator {
  height: 2px;
  background-color: #007bff;
  margin: 4px 0;
  border-radius: 1px;
}

/* 只读输入框样式 */
input[readonly] {
  color: #6c757d !important;
  background-color: #e9ecef !important;
}

.sequence-item.drag-over {
  border-top: 2px solid #007bff;
  margin-top: 2px;
}
</style> 